import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Sparkles, Heart, X, ChevronRight, RefreshCw } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { useChallengePreferences } from "@/hooks/useChallengePreferences";
import { useMemo, useState, useEffect } from "react";
import ChallengeModal from "./ChallengeModal";

interface Challenge {
  id: string;
  title: string;
  one_liner: string | null;
  status: string | null;
  difficulty: string | null;
  tags: string[] | null;
}

interface Recommendation {
  id: string;
  challenge_id: string;
  match_score: number;
  match_reason: string;
  generated_at: string;
  challenges: Challenge;
}

interface RecommendedChallengesCarouselProps {
  onViewMore: () => void;
}


export function RecommendedChallengesCarousel({ onViewMore }: RecommendedChallengesCarouselProps) {
  const { markInterested, markIgnored } = useChallengePreferences();
  const [selectedChallengeId, setSelectedChallengeId] = useState<string | null>(null);
  const [hasTriedGeneration, setHasTriedGeneration] = useState(false);
  const queryClient = useQueryClient();

  const { data: recommendations, isLoading } = useQuery({
    queryKey: ["challenge-recommendations-carousel"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const { data, error } = await supabase
        .from("challenge_recommendations")
        .select(`
          id,
          challenge_id,
          match_score,
          match_reason,
          generated_at,
          challenges (
            id,
            title,
            one_liner,
            status,
            difficulty,
            tags
          )
        `)
        .eq("user_id", user.id)
        .order("match_score", { ascending: false });

      if (error) throw error;
      return (data || []).filter((r) => r.challenges) as Recommendation[];
    },
  });

  // Mutation to generate recommendations
  const generateMutation = useMutation({
    mutationFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Not authenticated");
      
      const response = await supabase.functions.invoke("generate-recommendations", {
        headers: { Authorization: `Bearer ${session.access_token}` }
      });
      
      if (response.error) throw response.error;
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["challenge-recommendations-carousel"] });
    },
    onSettled: () => {
      setHasTriedGeneration(true);
    }
  });

  // Auto-trigger generation when no recommendations exist
  useEffect(() => {
    if (!isLoading && recommendations?.length === 0 && !generateMutation.isPending && !hasTriedGeneration) {
      generateMutation.mutate();
    }
  }, [isLoading, recommendations?.length, generateMutation.isPending, hasTriedGeneration]);

  // Keep recommendations sorted by match_score (highest first) from query
  const sortedRecommendations = useMemo(() => {
    if (!recommendations || recommendations.length === 0) return [];
    return recommendations.slice(0, 8); // Already sorted by match_score DESC from query
  }, [recommendations]);

  const getDifficultyColor = (difficulty: string | null) => {
    switch (difficulty) {
      case "beginner":
        return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300";
      case "intermediate":
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300";
      case "advanced":
        return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300";
      default:
        return "bg-muted text-muted-foreground";
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return "bg-green-500";
    if (score >= 60) return "bg-yellow-500";
    return "bg-orange-500";
  };

  const handleInterested = async (e: React.MouseEvent, challengeId: string) => {
    e.stopPropagation();
    await markInterested(challengeId);
  };

  const handleIgnore = async (e: React.MouseEvent, challengeId: string) => {
    e.stopPropagation();
    await markIgnored(challengeId);
  };

  if (isLoading || generateMutation.isPending) {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary animate-pulse" />
            <h3 className="text-lg font-semibold">
              {generateMutation.isPending ? "Finding challenges for you..." : "Recommended for You"}
            </h3>
          </div>
        </div>
        <div className="flex gap-4">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-40 w-64 flex-shrink-0" />
          ))}
        </div>
      </div>
    );
  }

  if (generateMutation.isError) {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            <h3 className="text-lg font-semibold">Recommended for You</h3>
          </div>
        </div>
        <Card className="p-6">
          <div className="text-center">
            <p className="text-muted-foreground mb-4">Couldn't load recommendations</p>
            <Button 
              variant="outline" 
              onClick={() => generateMutation.mutate()}
              disabled={generateMutation.isPending}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Try Again
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  if (!recommendations || recommendations.length === 0) {
    return null; // Only hide if generation completed with no results
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold">Recommended for You</h3>
        </div>
        <Button variant="link" className="text-primary p-0 h-auto" onClick={onViewMore}>
          View more <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </div>

      <div className="relative px-10">
        <Carousel
          opts={{
            align: "start",
            loop: false,
          }}
          className="w-full"
        >
          <CarouselContent className="-ml-2 md:-ml-4">
            {sortedRecommendations.map((rec) => (
              <CarouselItem
                key={rec.id}
                className="pl-2 md:pl-4 basis-4/5 sm:basis-1/2 md:basis-1/3 lg:basis-1/4"
              >
                <Card
                  className="cursor-pointer hover:shadow-md transition-shadow h-full"
                  onClick={() => setSelectedChallengeId(rec.challenge_id)}
                >
                  <CardContent className="p-4 flex flex-col h-full">
                    <div className="flex items-center justify-between mb-2">
                      <Badge className={`${getScoreColor(rec.match_score)} text-white text-xs`}>
                        {Math.round(rec.match_score)}% match
                      </Badge>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-muted-foreground hover:text-pink-500"
                          onClick={(e) => handleInterested(e, rec.challenge_id)}
                        >
                          <Heart className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-muted-foreground hover:text-destructive"
                          onClick={(e) => handleIgnore(e, rec.challenge_id)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    <h4 className="font-semibold text-sm line-clamp-2 mb-1">
                      {rec.challenges.title}
                    </h4>
                    <p className="text-xs text-muted-foreground line-clamp-2 flex-grow">
                      {rec.challenges.one_liner}
                    </p>

                    <div className="flex flex-wrap gap-1 mt-3">
                      {rec.challenges.difficulty && (
                        <Badge
                          variant="secondary"
                          className={`text-xs ${getDifficultyColor(rec.challenges.difficulty)}`}
                        >
                          {rec.challenges.difficulty}
                        </Badge>
                      )}
                      {rec.challenges.tags?.slice(0, 1).map((tag) => (
                        <Badge key={tag} variant="outline" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious className="left-0" />
          <CarouselNext className="right-0" />
        </Carousel>
      </div>

      {selectedChallengeId && (
        <ChallengeModal
          challengeId={selectedChallengeId}
          open={!!selectedChallengeId}
          onOpenChange={(open) => !open && setSelectedChallengeId(null)}
        />
      )}
    </div>
  );
}
